import React, { useState, useEffect, useMemo, startTransition, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Container, Typography, Tabs, Tab } from '@mui/material';
import Layout from '../components/Layout';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useFeedback } from '../context/FeedbackContext';
import { hasPermission } from '../utils/permissions';
import { ROLES } from '../constants/roles';
import { DIAMOND_TYPES } from '../constants/appConstants';
import { inventoryShapeOptions } from '../utils/shapeOptions';
import { InventoryShellContext } from '../context/InventoryShellContext';
import InventoryTabPanel from '../components/inventory/InventoryTabPanel';
import CertifiedInventory from './CertifiedInventory';
import LotsInventory from './LotsInventory';
import InventoryRequirements from './InventoryRequirements';
import {
  PATH_CERTIFIED,
  INVENTORY_TAB_PATHS,
  matchInventoryPanel,
  shouldBlockRequirementsPath,
  resolveBareRequirementsRedirect,
  isRequirementsPanel,
} from '../utils/inventoryRoutes';

const InventoryLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useFeedback();
  const { isInternal, isClient, userRole, customPermissions, refreshSession } = useAuth();

  const canSeeInventoryRequirements = isInternal && (
    userRole === ROLES.MANAGER
    || hasPermission(userRole, 'manage_inventory_requirements', customPermissions)
  );

  const activePanel = matchInventoryPanel(location.pathname);
  const isRequirementsView = isRequirementsPanel(activePanel);
  const inventoryTabIndex = INVENTORY_TAB_PATHS.findIndex((p) => location.pathname.startsWith(p));

  // ── Per-tab frozen search params ─────────────────────────────────────────
  // useSearchParams lives here (one subscription for all tabs).
  // Inactive tabs receive a frozen URLSearchParams object so React.memo can
  // bail out — preventing the full reconciliation cost on every tab switch.
  const [searchParams, setSearchParams] = useSearchParams();
  const certifiedSearchRef = useRef(activePanel === 'certified' ? searchParams : new URLSearchParams());
  const fancySearchRef = useRef(activePanel === 'fancy' ? searchParams : new URLSearchParams());
  const lotsSearchRef = useRef(activePanel === 'lots' ? searchParams : new URLSearchParams());
  if (activePanel === 'certified') certifiedSearchRef.current = searchParams;
  if (activePanel === 'fancy') fancySearchRef.current = searchParams;
  if (activePanel === 'lots') lotsSearchRef.current = searchParams;

  const [filterOptions, setFilterOptions] = useState({});
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);

  useEffect(() => {
    if (shouldBlockRequirementsPath(location.pathname, canSeeInventoryRequirements)) {
      navigate(PATH_CERTIFIED, { replace: true });
    }
  }, [location.pathname, canSeeInventoryRequirements, navigate]);

  useEffect(() => {
    const redirect = resolveBareRequirementsRedirect(location.pathname, canSeeInventoryRequirements);
    if (redirect) navigate(redirect, { replace: true });
  }, [location.pathname, canSeeInventoryRequirements, navigate]);

  useEffect(() => {
    if (location.pathname === '/inventory' || location.pathname === '/inventory/') {
      navigate(PATH_CERTIFIED, { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (isClient) refreshSession();
  }, [isClient, refreshSession]);

  useEffect(() => {
    const needsFilters = activePanel === 'certified' || activePanel === 'fancy' || activePanel === 'lots';
    if (!needsFilters || filterOptionsLoaded) return undefined;

    let cancelled = false;
    axios.get('/api/diamonds/filters/options')
      .then((res) => {
        if (!cancelled) {
          setFilterOptions({
            ...res.data,
            shapes: inventoryShapeOptions(res.data?.shapes),
            certifiedShapes: inventoryShapeOptions(res.data?.certifiedShapes),
            lotShapes: inventoryShapeOptions(res.data?.lotShapes),
          });
          setFilterOptionsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) showError(t('inventory.loadFailed'));
      });

    return () => { cancelled = true; };
  }, [activePanel, filterOptionsLoaded, showError, t]);

  const shellContext = useMemo(
    () => ({ filterOptions }),
    [filterOptions]
  );

  const pageTitle = useMemo(() => {
    if (activePanel === 'reqCertified') return t('inventory.requirementsCertified');
    if (activePanel === 'reqLots') return t('inventory.requirementsLots');
    return t('inventory.title');
  }, [activePanel, t]);

  const handleInventoryTabChange = useCallback((_, index) => {
    startTransition(() => navigate(INVENTORY_TAB_PATHS[index], { replace: true }));
  }, [navigate]);

  return (
    <Layout>
      <InventoryShellContext.Provider value={shellContext}>
        <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 2, sm: 3 } }}>
            {pageTitle}
          </Typography>

          {!isRequirementsView && (
            <Tabs
              value={inventoryTabIndex === -1 ? 0 : inventoryTabIndex}
              onChange={handleInventoryTabChange}
              sx={{ mb: 2 }}
            >
              <Tab label={t('inventory.certifiedDiamonds')} />
              <Tab label={t('inventory.fancyColors')} />
              <Tab label={t('inventory.nonCertifiedDiamonds')} />
            </Tabs>
          )}

          <InventoryTabPanel active={activePanel === 'certified'}>
            <CertifiedInventory
              colorCategory="standard"
              isTabActive={activePanel === 'certified'}
              searchParams={certifiedSearchRef.current}
              setSearchParams={setSearchParams}
            />
          </InventoryTabPanel>
          <InventoryTabPanel active={activePanel === 'fancy'}>
            <CertifiedInventory
              colorCategory="fancy"
              isTabActive={activePanel === 'fancy'}
              searchParams={fancySearchRef.current}
              setSearchParams={setSearchParams}
            />
          </InventoryTabPanel>
          <InventoryTabPanel active={activePanel === 'lots'}>
            <LotsInventory
              isTabActive={activePanel === 'lots'}
              searchParams={lotsSearchRef.current}
              setSearchParams={setSearchParams}
            />
          </InventoryTabPanel>
          {canSeeInventoryRequirements && (
            <>
              <InventoryTabPanel active={activePanel === 'reqCertified'}>
                <InventoryRequirements
                  scope={DIAMOND_TYPES.CERTIFIED}
                  isTabActive={activePanel === 'reqCertified'}
                />
              </InventoryTabPanel>
              <InventoryTabPanel active={activePanel === 'reqLots'}>
                <InventoryRequirements
                  scope={DIAMOND_TYPES.NON_CERTIFIED}
                  isTabActive={activePanel === 'reqLots'}
                />
              </InventoryTabPanel>
            </>
          )}
        </Container>
      </InventoryShellContext.Provider>
    </Layout>
  );
};

export default InventoryLayout;
